import * as THREE from 'three';
import type { WeaponKind } from './WeaponSystem.js';

/**
 * FpsViewmodelRig — animated first-person forearms + weapon attach point.
 *
 * Attach `rig.group` to the camera so it lives in view-space.
 * The existing viewModelGun group can be re-parented to `rig.weaponAttach`
 * or left as-is; this rig only manages the arm meshes & its own group.
 */
export class FpsViewmodelRig {
  /** Root group — add to camera with camera.add(rig.group) */
  readonly group = new THREE.Group();

  /** Weapon meshes should be added here (or your existing viewModelGun re-parented here) */
  readonly weaponAttach = new THREE.Group();

  // Arm meshes
  private forearmR!: THREE.Mesh;
  private forearmL!: THREE.Mesh;
  private handR!: THREE.Mesh;
  private handL!: THREE.Mesh;

  // One-shot timers (count down from value → 0)
  private recoilT = 0;       // decays over RECOIL_DUR
  private swapT = 0;         // decays over SWAP_DUR
  private swingT = 0;        // decays over SWING_DUR
  private adsSettleT = 0;    // decays over ADS_SETTLE_DUR
  private reloadTickT = 0;   // decays over RELOAD_TICK_DUR

  // Continuous blends
  private bobPhase = 0;
  private adsBlend = 0;
  private sprintTilt = 0;    // current z-roll
  private sprintDroop = 0;   // current x-droop

  // Target state
  private isAds = false;
  private isSprint = false;
  private dualWieldBlend = 0;
  private dualWieldTarget = 0;
  private dualWieldShotToggle = false;

  // Resting / ADS positions for the whole group
  private readonly REST_POS = new THREE.Vector3(0.18, -0.22, -0.38);
  private readonly ADS_POS  = new THREE.Vector3(0.0,  -0.09, -0.28);

  // Timings
  private static readonly RECOIL_DUR = 0.12;
  private static readonly SWAP_DUR   = 0.25;
  private static readonly SWING_DUR  = 0.25;
  private static readonly ADS_SETTLE_DUR = 0.18;
  private static readonly RELOAD_TICK_DUR = 0.14;

  constructor(_camera: THREE.Camera) {
    this.buildArms();
    this.group.add(this.weaponAttach);

    // Weapon attach sits a bit forward/down — the gun group plugs in here
    this.weaponAttach.position.set(0.0, -0.15, -0.30);

    // Start at rest position
    this.group.position.copy(this.REST_POS);
  }

  // ---------------------------------------------------------------------------
  // Build procedural arm geometry
  // ---------------------------------------------------------------------------

  private buildArms(): void {
    const skinMat = new THREE.MeshToonMaterial({ color: 0xd4a574 });
    const sleeveMat = new THREE.MeshToonMaterial({ color: 0x2a4a8a }); // blue sleeve

    const forearmGeo  = new THREE.BoxGeometry(0.055, 0.28, 0.055);
    const handGeo     = new THREE.BoxGeometry(0.07,  0.08, 0.045);
    const sleeveGeo   = new THREE.BoxGeometry(0.065, 0.14, 0.065);

    // Right forearm
    const sleeveR = new THREE.Mesh(sleeveGeo, sleeveMat);
    this.forearmR = new THREE.Mesh(forearmGeo, skinMat);
    this.handR    = new THREE.Mesh(handGeo,    skinMat);

    sleeveR.position.set(0, 0.07, 0);
    this.handR.position.set(0, -0.18, 0);

    const armGroupR = new THREE.Group();
    armGroupR.add(sleeveR, this.forearmR, this.handR);
    armGroupR.position.set(0.09, -0.08, -0.18);
    this.group.add(armGroupR);

    // Left forearm
    const sleeveL = new THREE.Mesh(sleeveGeo, sleeveMat);
    this.forearmL = new THREE.Mesh(forearmGeo, skinMat);
    this.handL    = new THREE.Mesh(handGeo,    skinMat);

    sleeveL.position.set(0, 0.07, 0);
    this.handL.position.set(0, -0.18, 0);

    const armGroupL = new THREE.Group();
    armGroupL.add(sleeveL, this.forearmL, this.handL);
    armGroupL.position.set(-0.09, -0.08, -0.18);
    // Slight inward rotation on left arm for natural grip pose
    armGroupL.rotation.z = 0.08;
    this.group.add(armGroupL);
  }

  // ---------------------------------------------------------------------------
  // Public triggers
  // ---------------------------------------------------------------------------

  /** Call after weapon.fire() — intensity 0..1 */
  triggerRecoil(intensity: number): void {
    this.recoilT = FpsViewmodelRig.RECOIL_DUR * Math.max(0.1, Math.min(1, intensity));
  }

  /** Call when weapon swap begins */
  triggerSwap(): void {
    this.swapT = FpsViewmodelRig.SWAP_DUR;
  }

  /** Call when pickaxe / melee swing begins */
  triggerSwing(): void {
    this.swingT = FpsViewmodelRig.SWING_DUR;
  }

  /** Call when ADS begins for a tiny settle pulse. */
  triggerAdsSettle(): void {
    this.adsSettleT = FpsViewmodelRig.ADS_SETTLE_DUR;
  }

  /** Call at the reload midpoint for a tactile magazine tick. */
  triggerReloadTick(): void {
    this.reloadTickT = FpsViewmodelRig.RELOAD_TICK_DUR;
  }

  /** Dual-pistol alt-fire — spread arms and offset weapon attach. */
  setDualWield(on: boolean, shotToggle = false): void {
    this.dualWieldTarget = on ? 1 : 0;
    if (on) this.dualWieldShotToggle = shotToggle;
  }

  // ---------------------------------------------------------------------------
  // Per-frame update
  // ---------------------------------------------------------------------------

  /**
   * @param dt          Delta time in seconds
   * @param velocity    Player rigidbody velocity
   * @param isGrounded  Whether player is on ground
   * @param isAds       Whether ADS is active
   * @param isSprint    Whether sprinting
   * @param reloadRatio 0..1 reload progress (unused for now, reserved)
   * @param reducedMotion Whether motion effects should be toned down
   */
  update(
    dt: number,
    velocity: THREE.Vector3,
    isGrounded: boolean,
    isAds: boolean,
    isSprint: boolean,
    _reloadRatio: number,
    reducedMotion = false,
  ): void {
    this.isAds    = isAds;
    this.isSprint = isSprint;

    const horizSpeed = Math.sqrt(velocity.x * velocity.x + velocity.z * velocity.z);
    const moving = isGrounded && horizSpeed > 0.5;

    // ── 1. Bob ──────────────────────────────────────────────────────────────
    if (moving) {
      this.bobPhase += dt * (8 + horizSpeed * 0.85);
    }
    const bobAmp  = isAds ? 0.3 : 1.0;
    const moveFactor = Math.min(1, horizSpeed / 5) * (moving ? 1 : 0);
    const motionScale = reducedMotion ? 0.35 : 1;
    const bobY = Math.sin(this.bobPhase)       * 0.012 * moveFactor * bobAmp * motionScale;
    const bobX = Math.sin(this.bobPhase * 0.5) * 0.008 * moveFactor * bobAmp * motionScale;
    const sway = reducedMotion ? 0 : moveFactor * (isAds ? 0.35 : 1);
    const swayRotY = Math.sin(this.bobPhase * 0.5 + 0.6) * 0.018 * sway;
    const swayRotZ = Math.sin(this.bobPhase + 1.2) * 0.012 * sway;

    // ── 2. ADS blend ────────────────────────────────────────────────────────
    const adsTarget = isAds ? 1 : 0;
    this.adsBlend += (adsTarget - this.adsBlend) * Math.min(1, 9 * dt);
    const ads = this.adsBlend;

    const targetPos = new THREE.Vector3().lerpVectors(this.REST_POS, this.ADS_POS, ads);
    targetPos.y += bobY;
    targetPos.x += bobX;

    // ── 3. Sprint tilt ──────────────────────────────────────────────────────
    const sprintTiltTarget  = isSprint ? 0.12 : 0;
    const sprintDroopTarget = isSprint ? 0.06 : 0;
    this.sprintTilt  += (sprintTiltTarget  - this.sprintTilt)  * Math.min(1, dt * 8);
    this.sprintDroop += (sprintDroopTarget - this.sprintDroop) * Math.min(1, dt * 8);

    // ── 4. One-shot: weapon swap slide-in ───────────────────────────────────
    let swapOffsetY   = 0;
    let swapRotX      = 0;
    if (this.swapT > 0) {
      this.swapT = Math.max(0, this.swapT - dt);
      const swapNorm = this.swapT / FpsViewmodelRig.SWAP_DUR; // 1→0 as swap completes
      const equipSnap = Math.sin((1 - swapNorm) * Math.PI);
      swapOffsetY = (swapNorm * 0.35 + equipSnap * 0.025) * motionScale;
      swapRotX    = (swapNorm * 0.8 - equipSnap * 0.08) * motionScale;
    }

    // ── 5. One-shot: recoil kickback ────────────────────────────────────────
    let recoilZ    = 0;
    let recoilRotX = 0;
    if (this.recoilT > 0) {
      this.recoilT = Math.max(0, this.recoilT - dt);
      const rNorm = this.recoilT / FpsViewmodelRig.RECOIL_DUR; // 1 at peak → 0
      recoilZ    = rNorm * 0.06 * motionScale;
      recoilRotX = rNorm * -0.18 * motionScale;
    }

    // ── 6. One-shot: melee swing arc ─────────────────────────────────────────
    let swingRotX = 0;
    if (this.swingT > 0) {
      this.swingT = Math.max(0, this.swingT - dt);
      const swingPhase = 1 - this.swingT / FpsViewmodelRig.SWING_DUR; // 0→1
      swingRotX = Math.sin(swingPhase * Math.PI) * -1.2 * motionScale;
    }

    // ── 7. One-shot: ADS settle / reload timing tick ─────────────────────────
    let adsSettleZ = 0;
    let adsSettleRotX = 0;
    if (this.adsSettleT > 0) {
      this.adsSettleT = Math.max(0, this.adsSettleT - dt);
      const phase = 1 - this.adsSettleT / FpsViewmodelRig.ADS_SETTLE_DUR;
      const pulse = Math.sin(phase * Math.PI) * motionScale;
      adsSettleZ = pulse * -0.018;
      adsSettleRotX = pulse * 0.035;
    }

    let reloadTickY = 0;
    let reloadTickRotZ = 0;
    if (this.reloadTickT > 0) {
      this.reloadTickT = Math.max(0, this.reloadTickT - dt);
      const phase = 1 - this.reloadTickT / FpsViewmodelRig.RELOAD_TICK_DUR;
      const tick = Math.sin(phase * Math.PI) * motionScale;
      reloadTickY = tick * -0.018;
      reloadTickRotZ = tick * -0.05;
    }

    this.dualWieldBlend += (this.dualWieldTarget - this.dualWieldBlend) * Math.min(1, 12 * dt);
    const dual = this.dualWieldBlend;
    const armSpread = dual * 0.05;
    if (dual > 0.02) {
      const side = this.dualWieldShotToggle ? 1 : -1;
      this.weaponAttach.position.x = side * 0.06 * dual;
      this.weaponAttach.rotation.z = side * 0.04 * dual;
    } else {
      this.weaponAttach.position.x = 0;
      this.weaponAttach.rotation.z = 0;
    }

    // ── Apply to group ───────────────────────────────────────────────────────
    // Position
    this.group.position.x = targetPos.x + armSpread;
    this.group.position.y = targetPos.y - swapOffsetY + reloadTickY;
    this.group.position.z = targetPos.z + recoilZ + adsSettleZ;

    // Rotation
    this.group.rotation.x = recoilRotX + swapRotX + swingRotX + this.sprintDroop + adsSettleRotX;
    this.group.rotation.z = this.sprintTilt + swayRotZ + reloadTickRotZ;
    this.group.rotation.y = swayRotY + dual * 0.03;

    const armGroups = this.group.children.filter((c) => c.type === 'Group' && c !== this.weaponAttach);
    if (armGroups[0]) armGroups[0].position.x = 0.09 + dual * 0.04;
    if (armGroups[1]) armGroups[1].position.x = -0.09 - dual * 0.04;
    if (armGroups[1]) armGroups[1].rotation.z = 0.08 + dual * 0.06;
  }

  // ---------------------------------------------------------------------------

  dispose(): void {
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
  }
}
